import { useEffect, type RefObject } from 'react';

/**
 * A visible focus ring on the message frame when the keyboard enters it
 * (WCAG 2.4.7, #205).
 *
 * The frame is a tab stop — Tab walks into the message to reach its links —
 * but CSS cannot show that: with focus inside a sandboxed, opaque-origin
 * document the <iframe> element matches neither :focus nor :focus-within in
 * the parent, and no focus event reaches the parent either. What the parent
 * does see is its own window losing focus while `document.activeElement` is
 * the frame. When a Tab keydown came just before, that is the keyboard, and
 * the frame gets `data-keyboard-focus` (message-frame.css draws the ring). A
 * click into the message moves focus the same way but without a Tab, so it
 * draws nothing, as :focus-visible would not.
 */
const TAB_WINDOW_MS = 1_000;

export function useFrameFocusRing(frameRef: RefObject<HTMLIFrameElement | null>): void {
  useEffect(() => {
    let lastTab = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') lastTab = Date.now();
    };
    const clear = () => frameRef.current?.removeAttribute('data-keyboard-focus');
    const onBlur = () => {
      const frame = frameRef.current;
      if (!frame) return;
      if (document.activeElement === frame && Date.now() - lastTab < TAB_WINDOW_MS) {
        frame.setAttribute('data-keyboard-focus', '');
      } else {
        clear();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', clear);
    document.addEventListener('focusin', clear, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', clear);
      document.removeEventListener('focusin', clear, true);
    };
  }, [frameRef]);
}
